/**
 * Bundle Checkout Integration
 * Wendet automatisch den Discount Code im Checkout an wenn Bundle-Produkte vorhanden sind
 */

class BundleCheckout {
  constructor() {
    this.isProcessing = false;
    this.init();
  }
  
  init() {
    debugLog('🎯 Bundle Checkout Integration initialisiert');
    
    // Wait a bit for DOM to be fully ready
    setTimeout(() => {
      this.interceptCheckoutButtons();
    }, 500);
    
    // Listen to cart updates
    document.documentElement.addEventListener('cart:refresh', () => {
      debugLog('🔄 Cart refresh - re-intercepting checkout buttons');
      setTimeout(() => {
        this.interceptCheckoutButtons();
      }, 300);
    });
    
    // Also listen to regular cart:updated
    document.documentElement.addEventListener('cart:updated', () => {
      debugLog('🔄 Cart updated - re-intercepting checkout buttons');
      setTimeout(() => {
        this.interceptCheckoutButtons();
      }, 300);
    });
  }
  
  interceptCheckoutButtons() {
    // Find all checkout buttons and forms - more specific selectors
    const checkoutButtons = document.querySelectorAll(
      'button[name="checkout"], ' +
      'button.checkout-button, ' +
      'button.cart__checkout-button, ' +
      '.checkout-button[type="submit"]'
    );
    
    const checkoutForms = document.querySelectorAll(
      'form[action*="/cart"], ' +
      'form#mini-cart-form'
    );
    
    debugLog(`🔍 Gefunden: ${checkoutButtons.length} Checkout-Buttons, ${checkoutForms.length} Forms`);
    
    // Log what we found
    checkoutButtons.forEach((btn, i) => {
      debugLog(`  Button ${i+1}:`, btn.className, btn.name);
    });
    
    checkoutForms.forEach((form, i) => {
      debugLog(`  Form ${i+1}:`, form.id, form.action);
    });
    
    // Mark forms as intercepted to avoid duplicate listeners
    checkoutForms.forEach(form => {
      if (form.dataset.bundleCheckoutIntercepted === 'true') {
        debugLog('⏭️ Form bereits intercepted, überspringe');
        return;
      }
      
      form.dataset.bundleCheckoutIntercepted = 'true';
      
      // Add event listener WITHOUT cloning (preserve other listeners)
      form.addEventListener('submit', (e) => {
        debugLog('📝 Form submit event triggered');
        this.handleFormSubmit(e, form);
      }, true); // Use capture phase to intercept early
    });
    
    // Add event listeners to buttons (secondary method)
    checkoutButtons.forEach(button => {
      if (button.dataset.bundleCheckoutIntercepted === 'true') {
        return;
      }
      
      button.dataset.bundleCheckoutIntercepted = 'true';
      
      button.addEventListener('click', (e) => {
        debugLog('🖱️ Button click event triggered:', button.className);
        // Only handle if it's NOT in a form (forms handle it)
        if (!button.form) {
          this.handleCheckoutClick(e, button);
        }
      }, true); // Use capture phase
    });
  }
  
  async handleCheckoutClick(event, button) {
    // Only handle links, not submit buttons
    if (button.tagName === 'A' && button.href.includes('/checkout')) {
      event.preventDefault();
      
      const discountCode = await this.getBundleDiscountCode();
      
      if (discountCode) {
        debugLog('✅ Leite weiter zum Checkout mit Code:', discountCode);
        window.location.href = `/checkout?discount=${encodeURIComponent(discountCode)}`;
      } else {
        debugLog('ℹ️ Keine Bundle-Produkte, normale Weiterleitung');
        window.location.href = button.href;
      }
    }
    // For submit buttons, let the form handler take care of it
  }
  
  async handleFormSubmit(event, form) {
    debugLog('🔍 handleFormSubmit aufgerufen');
    
    // Only handle checkout forms
    if (!form.querySelector('[name="checkout"]')) {
      debugLog('⚠️ Kein checkout input gefunden, ignoriere Form');
      return;
    }
    
    // Prevent double processing
    if (this.isProcessing) {
      debugLog('⏳ Bereits in Bearbeitung, verhindere Duplikat');
      event.preventDefault();
      return;
    }
    
    debugLog('🛑 Verhindere Standard-Submit');
    event.preventDefault();
    event.stopPropagation();
    
    this.isProcessing = true;
    
    try {
      const discountCode = await this.getBundleDiscountCode();
      
      if (discountCode) {
        debugLog('✅ Leite weiter zum Checkout mit Code:', discountCode);
        
        // Add discount code to checkout URL
        const checkoutUrl = `/checkout?discount=${encodeURIComponent(discountCode)}`;
        
        // Redirect to checkout with discount
        window.location.href = checkoutUrl;
      } else {
        debugLog('ℹ️ Keine Bundle-Produkte, normale Form-Submission');
        
        // Create a new form submission that bypasses our listener
        const newForm = document.createElement('form');
        newForm.method = 'POST';
        newForm.action = form.action;
        
        // Copy hidden inputs
        const checkoutInput = document.createElement('input');
        checkoutInput.type = 'hidden';
        checkoutInput.name = 'checkout';
        checkoutInput.value = '';
        newForm.appendChild(checkoutInput);
        
        document.body.appendChild(newForm);
        newForm.submit();
      }
    } catch (error) {
      debugError('❌ Fehler in handleFormSubmit:', error);
      // Fallback: normal submission
      const newForm = form.cloneNode(true);
      document.body.appendChild(newForm);
      newForm.submit();
    } finally {
      setTimeout(() => {
        this.isProcessing = false;
      }, 1000);
    }
  }
  
  async getBundleDiscountCode() {
    try {
      // Get current cart
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      debugLog('🛒 Prüfe Warenkorb auf Bundle-Produkte...');
      
      // Check if cart has bundle products
      const bundleItems = cart.items.filter(item => 
        item.properties && item.properties._bundle_id
      );
      
      if (bundleItems.length === 0) {
        debugLog('📭 Keine Bundle-Produkte im Warenkorb');
        return null;
      }
      
      debugLog(`📦 ${bundleItems.length} Bundle-Produkte gefunden`);
      
      // Get discount code from first bundle item
      const discountCode = bundleItems[0].properties._discount_code;
      
      if (!discountCode) {
        debugWarn('⚠️ Bundle-Produkt hat keinen Discount Code!');
        return null;
      }
      
      debugLog(`💰 Discount Code: ${discountCode}`);
      
      // Validate bundle integrity before applying code
      const isValid = await this.validateBundleIntegrity(cart.items, bundleItems);
      
      if (!isValid) {
        debugWarn('⚠️ Bundle ist nicht vollständig - kein Code angewendet');
        return null;
      }
      
      return discountCode;
      
    } catch (error) {
      debugError('❌ Fehler beim Abrufen des Discount Codes:', error);
      return null;
    }
  }
  
  async validateBundleIntegrity(allItems, bundleItems) {
    // Group by bundle ID
    const bundles = {};
    
    bundleItems.forEach(item => {
      const bundleId = item.properties._bundle_id;
      const expectedCount = parseInt(item.properties._bundle_count);
      
      if (!bundles[bundleId]) {
        bundles[bundleId] = {
          items: [],
          expectedCount: expectedCount
        };
      }
      
      bundles[bundleId].items.push(item);
    });
    
    // Check each bundle
    let allValid = true;
    
    Object.entries(bundles).forEach(([bundleId, bundle]) => {
      const actualCount = bundle.items.length;
      const expectedCount = bundle.expectedCount;
      
      debugLog(`📊 Bundle ${bundleId.substring(0, 15)}... - Erwartet: ${expectedCount}, Vorhanden: ${actualCount}`);
      
      if (actualCount !== expectedCount) {
        debugWarn(`❌ Bundle ${bundleId} ist unvollständig!`);
        allValid = false;
      }
    });
    
    return allValid;
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.BundleCheckout = new BundleCheckout();
  });
} else {
  window.BundleCheckout = new BundleCheckout();
}

// Export for use in other scripts
window.BundleCheckout = BundleCheckout;
